import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Tag } from "lucide-react";
import { BlogLayout, BlogMeta } from "./BlogLayout";
import { getBlogPosts, getRelatedPosts } from "../services/blog/blogService";
import { BlogPost } from "../../types/blog.types";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { BlogAdBanner, BlogAdInContent } from "./AdComponent";

interface BlogPostViewProps {
  post: BlogPost;
  onBack: () => void;
  onPostClick: (post: BlogPost) => void;
}

export const BlogPostView = React.memo(({ post, onBack, onPostClick }: BlogPostViewProps) => {
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const posts = await getBlogPosts();
        setAllPosts(posts);
      } catch (error) {
        console.error('Error loading posts for related posts:', error);
      } finally {
        setIsLoadingRelated(false);
      }
    };
    loadPosts();
  }, []);

  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    const loadRelated = async () => {
      try {
        const related = await getRelatedPosts(post, 3);
        setRelatedPosts(related);
      } catch (error) {
        console.error('Error loading related posts:', error);
        setRelatedPosts([]);
      }
    };
    loadRelated();
  }, [post]);

  const handleRelatedPostClick = useCallback((relatedPost: BlogPost) => {
    onPostClick(relatedPost);
  }, [onPostClick]);

  return (
    <BlogLayout onBack={onBack} showBackButton={true}>
      <article className="max-w-3xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
            {post.title}
          </h1>
          
          <BlogMeta
            author={post.author}
            publishedAt={post.publishedAt}
            readTime={post.readTime}
            category={post.category.replace('-', ' ').toUpperCase()}
            tags={post.tags}
          />
        </header>

        {/* Top Banner Ad */}
        <BlogAdBanner />

        {/* Content */}
        <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({ children, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
                <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props}>{children}</h1>
              ),
              h2: ({ children, ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
                <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-3" {...props}>{children}</h2>
              ),
              h3: ({ children, ...props }: React.ComponentPropsWithoutRef<'h3'>) => (
                <h3 className="text-xl font-bold text-gray-900 mt-4 mb-2" {...props}>{children}</h3>
              ),
              h4: ({ children, ...props }: React.ComponentPropsWithoutRef<'h4'>) => (
                <h4 className="text-lg font-bold text-gray-900 mt-3 mb-2" {...props}>{children}</h4>
              ),
              p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
                <p className="mb-3" {...props}>{children}</p>
              ),
              ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
                <ul className="pl-0 mb-3 space-y-1 list-none" {...props}>{children}</ul>
              ),
              ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
                <ol className="list-decimal pl-6 mb-4 space-y-1" {...props}>{children}</ol>
              ),
              li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => {
                const parts = React.Children.toArray(children);
                const text = parts.map((p: any) => (typeof p === 'string' ? p : (p?.props?.children ?? ''))).join('');
                const trimmed = text.trim();
                const hasCheck = trimmed.startsWith('✅');
                const hasCross = trimmed.startsWith('❌');
                const cleanText = trimmed.replace(/^[✅❌]\s*/, '');
                if (hasCheck || hasCross) {
                  return (
                    <li className="my-0.5 flex items-start" {...props}>
                      <span className={"mr-1 mt-[2px] leading-none " + (hasCheck ? 'text-green-600' : 'text-red-600')}>
                        {hasCheck ? '✅' : '❌'}
                      </span>
                      <span className="flex-1 leading-relaxed">{cleanText}</span>
                    </li>
                  );
                }
                return <li className="my-0.5 list-disc list-inside pl-4" {...props}>{children}</li>;
              },
              hr: (props: React.ComponentPropsWithoutRef<'hr'>) => (
                <hr className="my-8 border-t-2 border-gray-300" {...props} />
              ),
              blockquote: ({ children, ...props }: React.ComponentPropsWithoutRef<'blockquote'>) => (
                <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-4" {...props}>{children}</blockquote>
              ),
              code: ({ children, ...props }: React.ComponentPropsWithoutRef<'code'>) => (
                <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded" {...props}>{children}</code>
              ),
              pre: ({ children, ...props }: React.ComponentPropsWithoutRef<'pre'>) => (
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4" {...props}>{children}</pre>
              ),
              table: ({ children, ...props }: React.ComponentPropsWithoutRef<'table'>) => (
                <table className="w-full border border-gray-200 rounded-lg text-sm border-collapse" {...props}>{children}</table>
              ),
              thead: ({ children, ...props }: React.ComponentPropsWithoutRef<'thead'>) => (
                <thead className="bg-gray-50" {...props}>{children}</thead>
              ),
              th: ({ children, ...props }: React.ComponentPropsWithoutRef<'th'>) => (
                <th className="px-4 py-2 text-gray-900 font-semibold border-b border-gray-200 text-left" {...props}>{children}</th>
              ),
              td: ({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) => (
                <td className="px-4 py-2 text-gray-800 border-b border-gray-100 align-top" {...props}>{children}</td>
              ),
              a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
                <a
                  href={href ?? undefined}
                  className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                  {...props}
                >
                  {children}
                </a>
              ),
              img: ({ src, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
                <img src={src || ''} alt={alt || ''} className="w-full max-w-2xl mx-auto rounded-lg shadow-md my-6" loading="lazy" {...props} />
              ),
            }}
          >
            {preprocessContent(post.content)}
          </ReactMarkdown>
        </div>

        {/* In-Content Ad */}
        <BlogAdInContent />

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-gray-600" />
              <span className="font-semibold text-gray-900">Tags</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Related Posts */}
        {isLoadingRelated ? (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading related articles...</span>
            </div>
          </div>
        ) : relatedPosts.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="space-y-3">
              {relatedPosts.map((relatedPost) => (
                <div key={relatedPost.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <button
                      onClick={() => handleRelatedPostClick(relatedPost)}
                      className="text-left w-full"
                    >
                      <h4 className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors mb-1">
                        {relatedPost.title}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </button>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {relatedPost.category.replace('-', ' ').toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {relatedPost.readTime} min read
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    </BlogLayout>
  );
});

// Small preprocessor for custom shortcodes (e.g., YouTube embeds)
function preprocessContent(content: string): string {
  return content.replace(/\[YouTube:\s*([a-zA-Z0-9_-]+)(?:\s+"([^"]*)")?\]/g,
    '<div class="my-8"><div class="relative w-full max-w-4xl mx-auto" style="padding-bottom: 56.25%;"><iframe class="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg" src="https://www.youtube.com/embed/$1" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div></div>'
  );
}
